// First Created - 19 Aug 2026
// Author - Atul Pandey
// Purpose - To update an Org Trail Session Challenge Assignment
// Updated - 19 Aug 2026 - assignment context remains fixed;
//                         audience, users, status, availability,
//                         reason and active state may be updated

import React, {
    useMemo,
    useState,
    useEffect,
} from "react"

import {
    useNavigate,
} from "react-router"

import useTitle from "@/hooks/useTitle"

import EtBtnFormBasic
    from "@/components/common/buttons/EtBtnFormBasic"

import PulseLoader
    from "react-spinners/PulseLoader"

import {
    useGetOrgTrailSessionChallengeAssignmentsQuery,
    useUpsertOrgTrailSessionChallengeAssignmentMutation,
} from "./orgTrailSessionChallengeAssignmentsApiSlice"

/*
|--------------------------------------------------------------------------
| 01 - Resolve Populated Or Plain ID
|--------------------------------------------------------------------------
*/

const getRefId = (
    value
) => {
    if (
        !value
    ) {
        return ""
    }

    if (
        typeof value ===
        "string"
    ) {
        return value
    }

    return (
        value?._id ||
        value?.id ||
        ""
    )
}

/*
|--------------------------------------------------------------------------
| 02 - User Display Name
|--------------------------------------------------------------------------
*/

const getUserDisplayName = (
    user
) => {
    return (
        user?.name ||
        user?.username ||
        user?.email ||
        "User"
    )
}

/*
|--------------------------------------------------------------------------
| 03 - Format Date For datetime-local
|--------------------------------------------------------------------------
*/

const formatDateTimeForInput = (
    value
) => {
    if (
        !value
    ) {
        return ""
    }

    const date =
        new Date(
            value
        )

    if (
        Number.isNaN(
            date.getTime()
        )
    ) {
        return ""
    }

    /*
    |--------------------------------------------------------------------------
    | Convert To Local datetime-local Format
    |--------------------------------------------------------------------------
    |
    | YYYY-MM-DDTHH:mm
    |
    |--------------------------------------------------------------------------
    */

    const offset =
        date.getTimezoneOffset()

    const localDate =
        new Date(
            date.getTime() -
            offset * 60 * 1000
        )

    return localDate
        .toISOString()
        .slice(
            0,
            16
        )
}

/*
|--------------------------------------------------------------------------
| 04 - Normalize Current Selected User IDs
|--------------------------------------------------------------------------
*/

const normalizeSelectedUserIds = (
    selectedUserIds
) => {
    if (
        !Array.isArray(
            selectedUserIds
        )
    ) {
        return []
    }

    return selectedUserIds
        .filter(
            item =>
                item?.userId &&
                item?.active !==
                false
        )
        .map(
            item =>
                getRefId(
                    item.userId
                )
        )
        .filter(
            Boolean
        )
}

/*
|--------------------------------------------------------------------------
| Main Component
|--------------------------------------------------------------------------
*/

const EtUpdateOrgTrailSessionChallengeAssignment = ({
    id,
    setActiveAdminView,
}) => {

    // First Created - 19 Aug 2026
    // Author - Atul Pandey
    // Purpose - Update existing Session Challenge assignment / metering

    useTitle(
        "Update Session Challenge Assignment"
    )

    /*
    |--------------------------------------------------------------------------
    | 05 - Get Existing Assignment From List Cache
    |--------------------------------------------------------------------------
    */

    const {
        assignment,
        isLoading,
        isFetching,
    } =
        useGetOrgTrailSessionChallengeAssignmentsQuery(
            {
                active:
                    true,
            },
            {
                selectFromResult: ({
                    data,
                    isLoading,
                    isFetching,
                }) => ({
                    assignment:
                        data
                            ?.entities[
                            id
                        ],

                    isLoading,

                    isFetching,
                }),
            }
        )

    /*
    |--------------------------------------------------------------------------
    | 06 - Loading
    |--------------------------------------------------------------------------
    */

    if (
        isLoading ||
        isFetching ||
        !assignment
    ) {
        return (
            <PulseLoader
                color={
                    "#ea580c"
                }
            />
        )
    }

    return (
        <EtUpdateOrgTrailSessionChallengeAssignmentForm
            assignment={
                assignment
            }

            id={
                id
            }

            setActiveAdminView={
                setActiveAdminView
            }
        />
    )
}

/*
|--------------------------------------------------------------------------
| Update Form
|--------------------------------------------------------------------------
*/

const EtUpdateOrgTrailSessionChallengeAssignmentForm = ({
    assignment,
    id,
    setActiveAdminView,
}) => {

    const navigate =
        useNavigate()

    /*
    |--------------------------------------------------------------------------
    | 07 - Mutation
    |--------------------------------------------------------------------------
    */

    const [
        upsertOrgTrailSessionChallengeAssignment,
        {
            isLoading,
            isSuccess,
            isError,
            error,
        },
    ] =
        useUpsertOrgTrailSessionChallengeAssignmentMutation()

    /*
    |--------------------------------------------------------------------------
    | 08 - Fixed Assignment Context
    |--------------------------------------------------------------------------
    */

    const orgId =
        getRefId(
            assignment
                ?.orgId
        )

    const trailId =
        getRefId(
            assignment
                ?.trailId
        )

    const cohortId =
        getRefId(
            assignment
                ?.cohortId
        )

    const sessionId =
        getRefId(
            assignment
                ?.sessionId
        )

    const challengeId =
        getRefId(
            assignment
                ?.challengeId
        )

    /*
    |--------------------------------------------------------------------------
    | 09 - Initial Form State
    |--------------------------------------------------------------------------
    */

    const initialFormState =
        useMemo(
            () => ({
                audienceType:
                    assignment
                        ?.audienceType ||
                    "AllCohortUsers",

                selectedUserIds:
                    normalizeSelectedUserIds(
                        assignment
                            ?.selectedUserIds
                    ),

                assignmentStatus:
                    assignment
                        ?.assignmentStatus ||
                    "Open",

                availableFrom:
                    formatDateTimeForInput(
                        assignment
                            ?.availableFrom
                    ),

                availableUntil:
                    formatDateTimeForInput(
                        assignment
                            ?.availableUntil
                    ),

                assignmentReason:
                    assignment
                        ?.assignmentReason ||
                    "",

                active:
                    assignment
                        ?.active !==
                    false,
            }),
            [
                assignment,
            ]
        )

    const [
        formData,
        setFormData,
    ] =
        useState(
            initialFormState
        )

    /*
    |--------------------------------------------------------------------------
    | 10 - Return To List On Success
    |--------------------------------------------------------------------------
    */

    useEffect(
        () => {
            if (
                isSuccess
            ) {
                if (
                    setActiveAdminView
                ) {
                    setActiveAdminView(
                        "orgTrailSessionChallengeAssignments"
                    )
                } else {
                    navigate(
                        "/orgTrailSessionChallengeAssignments"
                    )
                }
            }
        },
        [
            isSuccess,
            navigate,
            setActiveAdminView,
        ]
    )

    /*
    |--------------------------------------------------------------------------
    | 11 - Eligible Cohort Users
    |--------------------------------------------------------------------------
    |
    | OrgTrailSessionCohort remains the source of truth.
    |
    | The populated cohortId contains cohortUserIds.
    |
    |--------------------------------------------------------------------------
    */

    const eligibleCohortUsers =
        useMemo(
            () => {
                const cohortUsers =
                    assignment
                        ?.cohortId
                        ?.cohortUserIds ||
                    []

                return cohortUsers
                    .filter(
                        item =>
                            item
                                ?.userId &&
                            item
                                ?.active !==
                                false &&
                            ![
                                "Dropped",
                                "Archived",
                                "Transferred",
                            ].includes(
                                item
                                    ?.userCohortStatus
                            )
                    )
                    .sort(
                        (
                            a,
                            b
                        ) =>
                            getUserDisplayName(
                                a?.userId
                            )
                                .localeCompare(
                                    getUserDisplayName(
                                        b?.userId
                                    )
                                )
                    )
            },
            [
                assignment,
            ]
        )

    /*
    |--------------------------------------------------------------------------
    | 12 - Existing Change Log
    |--------------------------------------------------------------------------
    */

    const assignmentChangeLog =
        useMemo(
            () =>
                Array.isArray(
                    assignment
                        ?.assignmentChangeLog
                )
                    ?
                    [
                        ...assignment
                            .assignmentChangeLog,
                    ]
                        .sort(
                            (
                                a,
                                b
                            ) =>
                                (
                                    b
                                        ?.changeNumber ||
                                    0
                                ) -
                                (
                                    a
                                        ?.changeNumber ||
                                    0
                                )
                        )
                    :
                    [],
            [
                assignment,
            ]
        )

    /*
    |--------------------------------------------------------------------------
    | 13 - Handle General Change
    |--------------------------------------------------------------------------
    */

    const handleChange = (
        e
    ) => {
        const {
            name,
            value,
            type,
            checked,
        } =
            e.target

        if (
            type ===
            "checkbox"
        ) {
            setFormData(
                prev => ({
                    ...prev,

                    [name]:
                        checked,
                })
            )

            return
        }

        setFormData(
            prev => ({
                ...prev,

                [name]:
                    value,

                /*
                |--------------------------------------------------------------------------
                | AllCohortUsers Should Not Retain Selected Users
                |--------------------------------------------------------------------------
                */

                ...(name ===
                    "audienceType" &&
                value ===
                    "AllCohortUsers"
                    ?
                    {
                        selectedUserIds:
                            [],
                    }
                    :
                    {}),
            })
        )
    }

    /*
    |--------------------------------------------------------------------------
    | 14 - Toggle Selected User
    |--------------------------------------------------------------------------
    */

    const handleSelectedUserToggle = (
        userId
    ) => {
        const exists =
            formData
                .selectedUserIds
                .some(
                    selectedId =>
                        selectedId
                            ?.toString() ===
                        userId
                            ?.toString()
                )

        const updatedUserIds =
            exists
                ?
                formData
                    .selectedUserIds
                    .filter(
                        selectedId =>
                            selectedId
                                ?.toString() !==
                            userId
                                ?.toString()
                    )
                :
                [
                    ...formData
                        .selectedUserIds,

                    userId,
                ]

        setFormData(
            prev => ({
                ...prev,

                selectedUserIds:
                    updatedUserIds,
            })
        )
    }

    /*
    |--------------------------------------------------------------------------
    | 15 - Select All
    |--------------------------------------------------------------------------
    */

    const handleSelectAllUsers = () => {
        const allUserIds =
            eligibleCohortUsers
                .map(
                    item =>
                        getRefId(
                            item
                                ?.userId
                        )
                )
                .filter(
                    Boolean
                )

        setFormData(
            prev => ({
                ...prev,

                selectedUserIds:
                    allUserIds,
            })
        )
    }

    /*
    |--------------------------------------------------------------------------
    | 16 - Clear Selected Users
    |--------------------------------------------------------------------------
    */

    const handleClearSelectedUsers = () => {
        setFormData(
            prev => ({
                ...prev,

                selectedUserIds:
                    [],
            })
        )
    }

    /*
    |--------------------------------------------------------------------------
    | 17 - Submit
    |--------------------------------------------------------------------------
    */

    const handleSubmit =
        async (
            e
        ) => {
            e.preventDefault()

            /*
            |--------------------------------------------------------------------------
            | Selected Users Validation
            |--------------------------------------------------------------------------
            */

            if (
                formData
                    .audienceType ===
                    "SelectedUsers" &&
                formData
                    .selectedUserIds
                    .length ===
                    0
            ) {
                alert(
                    "Select at least one cohort user."
                )

                return
            }

            /*
            |--------------------------------------------------------------------------
            | Availability Validation
            |--------------------------------------------------------------------------
            */

            if (
                formData
                    .availableFrom &&
                formData
                    .availableUntil &&
                new Date(
                    formData
                        .availableUntil
                ) <
                new Date(
                    formData
                        .availableFrom
                )
            ) {
                alert(
                    "Available Until cannot be earlier than Available From."
                )

                return
            }

            /*
            |--------------------------------------------------------------------------
            | Complete Desired Assignment State
            |--------------------------------------------------------------------------
            |
            | Same upsert endpoint is used for create and update.
            |
            | The backend calculates:
            |
            | previousState
            | currentState
            | added users
            | removed users
            | change type
            | change number
            |
            |--------------------------------------------------------------------------
            */

            const assignmentData = {
                orgId,

                trailId,

                sessionId,

                challengeId,

                cohortId,

                audienceType:
                    formData
                        .audienceType,

                selectedUserIds:
                    formData
                        .audienceType ===
                        "SelectedUsers"
                        ?
                        formData
                            .selectedUserIds
                        :
                        [],

                assignmentStatus:
                    formData
                        .assignmentStatus,

                availableFrom:
                    formData
                        .availableFrom ||
                    undefined,

                availableUntil:
                    formData
                        .availableUntil ||
                    undefined,

                assignmentReason:
                    formData
                        .assignmentReason,

                active:
                    formData
                        .active,
            }

            try {
                const response =
                    await upsertOrgTrailSessionChallengeAssignment(
                        assignmentData
                    )
                        .unwrap()

                alert(
                    response
                        ?.message ||
                    "Session Challenge assignment updated successfully"
                )
            } catch (
                submitError
            ) {
                console.error(
                    submitError
                )

                alert(
                    submitError
                        ?.data
                        ?.message ||
                    "Failed to update Session Challenge assignment"
                )
            }
        }

    /*
    |--------------------------------------------------------------------------
    | 18 - Cancel
    |--------------------------------------------------------------------------
    */

    const handleCancel = () => {
        if (
            setActiveAdminView
        ) {
            setActiveAdminView(
                "orgTrailSessionChallengeAssignments"
            )
        } else {
            navigate(
                "/orgTrailSessionChallengeAssignments"
            )
        }
    }

    /*
    |--------------------------------------------------------------------------
    | 19 - Render
    |--------------------------------------------------------------------------
    */

    return (
        <div
            className="bg-gray-200 p-1 mx-auto mt-5 rounded-lg shadow-md shadow-black"
        >

            {
                isLoading &&
                (
                    <PulseLoader
                        color={
                            "#ea580c"
                        }
                    />
                )
            }

            {
                isError &&
                (
                    <p
                        className="text-orange-700 font-semi-bold py-2"
                    >
                        {
                            error
                                ?.data
                                ?.message
                        }
                    </p>
                )
            }

            <div
                className="bg-white p-1 m-1 rounded"
            >

                <p
                    className="p-1 m-1 font-bold font-poppins text-xl"
                >
                    Update Session Challenge Assignment
                </p>

                <div
                    className="bg-gray-200 rounded m-1 p-1"
                >

                    <form
                        onSubmit={
                            handleSubmit
                        }

                        className="m-1 p-1 text-xs font-poppins"
                    >

                        {/* 1 - Organization */}

                        <div
                            className="bg-white rounded m-1 p-2"
                        >
                            <p
                                className="font-bold text-xs m-1"
                            >
                                Organization
                            </p>

                            <input
                                type="text"

                                value={
                                    assignment
                                        ?.orgId
                                        ?.organizationName ||
                                    "Organization not defined"
                                }

                                disabled

                                className="w-full rounded p-2 m-1 bg-gray-100 text-gray-600 cursor-not-allowed"
                            />
                        </div>

                        {/* 2 - Trail */}

                        <div
                            className="bg-white rounded m-1 p-2"
                        >
                            <p
                                className="font-bold text-xs m-1"
                            >
                                Trail
                            </p>

                            <input
                                type="text"

                                value={
                                    assignment
                                        ?.trailId
                                        ?.trailName ||
                                    "Trail not defined"
                                }

                                disabled

                                className="w-full rounded p-2 m-1 bg-gray-100 text-gray-600 cursor-not-allowed"
                            />
                        </div>

                        {/* 3 - Cohort */}

                        <div
                            className="bg-white rounded m-1 p-2"
                        >

                            <div
                                className="flex justify-between items-center"
                            >
                                <p
                                    className="font-bold text-xs m-1"
                                >
                                    Cohort
                                </p>

                                <p
                                    className="text-[10px] bg-orange-100 text-orange-700 rounded-full mx-2 px-2 py-1"
                                >
                                    Eligible Users:{" "}
                                    {
                                        eligibleCohortUsers
                                            .length
                                    }
                                </p>
                            </div>

                            <input
                                type="text"

                                value={
                                    assignment
                                        ?.cohortId
                                        ?.cohortName ||
                                    assignment
                                        ?.cohortId
                                        ?.cohortCode ||
                                    "Cohort not defined"
                                }

                                disabled

                                className="w-full rounded p-2 m-1 bg-gray-100 text-gray-600 cursor-not-allowed"
                            />
                        </div>

                        {/* 4 - Session */}

                        <div
                            className="bg-white rounded m-1 p-2"
                        >
                            <p
                                className="font-bold text-xs m-1"
                            >
                                Session
                            </p>

                            <input
                                type="text"

                                value={
                                    assignment
                                        ?.sessionId
                                        ?.sessionName ||
                                    "Session not defined"
                                }

                                disabled

                                className="w-full rounded p-2 m-1 bg-gray-100 text-gray-600 cursor-not-allowed"
                            />
                        </div>

                        {/* 5 - Challenge */}

                        <div
                            className="bg-white rounded m-1 p-2"
                        >

                            <p
                                className="font-bold text-xs m-1"
                            >
                                Session Challenge
                            </p>

                            <input
                                type="text"

                                value={
                                    assignment
                                        ?.challengeId
                                        ?.challengeName ||
                                    "Challenge not defined"
                                }

                                disabled

                                className="w-full rounded p-2 m-1 bg-gray-100 text-gray-600 cursor-not-allowed"
                            />

                            {
                                assignment
                                    ?.challengeId
                                    ?.challengeDesc &&
                                (
                                    <p
                                        className="text-[9px] text-gray-500 mx-2 my-1"
                                    >
                                        {
                                            assignment
                                                .challengeId
                                                .challengeDesc
                                        }
                                    </p>
                                )
                            }
                        </div>

                        {/* 6 - Audience */}

                        <div
                            className="bg-white rounded m-1 p-2"
                        >

                            <p
                                className="font-bold text-xs m-1"
                            >
                                Challenge Audience
                            </p>

                            <p
                                className="text-[8px] m-1 italic text-gray-600"
                            >
                                Update whether the challenge is available to the full cohort or only selected cohort users.
                            </p>

                            <div
                                className="grid grid-cols-1 md:grid-cols-2 gap-2 m-1"
                            >

                                {/* All Cohort Users */}

                                <label
                                    className={`rounded p-3 cursor-pointer border ${
                                        formData
                                            .audienceType ===
                                            "AllCohortUsers"
                                            ?
                                            "border-orange-600 bg-orange-50"
                                            :
                                            "border-gray-200 bg-gray-100"
                                    }`}
                                >

                                    <div
                                        className="flex justify-start items-center"
                                    >
                                        <input
                                            type="radio"

                                            name="audienceType"

                                            value="AllCohortUsers"

                                            checked={
                                                formData
                                                    .audienceType ===
                                                "AllCohortUsers"
                                            }

                                            onChange={
                                                handleChange
                                            }

                                            className="m-1"
                                        />

                                        <div
                                            className="mx-1"
                                        >

                                            <p
                                                className="text-xs font-bold"
                                            >
                                                All Cohort Users
                                            </p>

                                            <p
                                                className="text-[9px] text-gray-500"
                                            >
                                                Everyone currently eligible in the cohort.
                                            </p>
                                        </div>
                                    </div>
                                </label>

                                {/* Selected Users */}

                                <label
                                    className={`rounded p-3 cursor-pointer border ${
                                        formData
                                            .audienceType ===
                                            "SelectedUsers"
                                            ?
                                            "border-orange-600 bg-orange-50"
                                            :
                                            "border-gray-200 bg-gray-100"
                                    }`}
                                >

                                    <div
                                        className="flex justify-start items-center"
                                    >
                                        <input
                                            type="radio"

                                            name="audienceType"

                                            value="SelectedUsers"

                                            checked={
                                                formData
                                                    .audienceType ===
                                                "SelectedUsers"
                                            }

                                            onChange={
                                                handleChange
                                            }

                                            className="m-1"
                                        />

                                        <div
                                            className="mx-1"
                                        >

                                            <p
                                                className="text-xs font-bold"
                                            >
                                                Selected Users
                                            </p>

                                            <p
                                                className="text-[9px] text-gray-500"
                                            >
                                                Meter this challenge to selected users only.
                                            </p>
                                        </div>
                                    </div>
                                </label>
                            </div>
                        </div>

                        {/* 7 - Selected Users */}

                        {
                            formData
                                .audienceType ===
                                "SelectedUsers" &&
                            (
                                <div
                                    className="bg-white rounded m-1 p-2"
                                >

                                    <div
                                        className="flex justify-between items-center"
                                    >

                                        <div>
                                            <p
                                                className="font-bold text-xs m-1"
                                            >
                                                Selected Cohort Users
                                            </p>

                                            <p
                                                className="text-[8px] m-1 italic text-gray-600"
                                            >
                                                Changes made here will be captured in the assignment change log as added and removed users.
                                            </p>
                                        </div>

                                        <div
                                            className="flex justify-end items-center"
                                        >

                                            <p
                                                className="text-[10px] bg-orange-100 text-orange-700 rounded-full mx-2 px-2 py-1"
                                            >
                                                Selected:{" "}
                                                {
                                                    formData
                                                        .selectedUserIds
                                                        .length
                                                }
                                                {" / "}
                                                {
                                                    eligibleCohortUsers
                                                        .length
                                                }
                                            </p>

                                            <button
                                                type="button"

                                                onClick={
                                                    handleSelectAllUsers
                                                }

                                                className="text-[10px] bg-green-600 text-white rounded-full mx-1 px-3 py-1"
                                            >
                                                Select All
                                            </button>

                                            <button
                                                type="button"

                                                onClick={
                                                    handleClearSelectedUsers
                                                }

                                                className="text-[10px] bg-red-500 text-white rounded-full mx-1 px-3 py-1"
                                            >
                                                Clear Users
                                            </button>

                                        </div>
                                    </div>

                                    <div
                                        className="grid grid-cols-1 md:grid-cols-2 gap-1 max-h-96 overflow-y-auto"
                                    >
                                        {
                                            eligibleCohortUsers.map(
                                                (
                                                    cohortUserItem,
                                                    index
                                                ) => {

                                                    const user =
                                                        cohortUserItem
                                                            ?.userId

                                                    const userId =
                                                        getRefId(
                                                            user
                                                        )

                                                    const isChecked =
                                                        formData
                                                            .selectedUserIds
                                                            .some(
                                                                selectedId =>
                                                                    selectedId
                                                                        ?.toString() ===
                                                                    userId
                                                                        ?.toString()
                                                            )

                                                    return (
                                                        <div
                                                            key={
                                                                userId ||
                                                                index
                                                            }

                                                            className={`flex justify-between items-center rounded m-1 p-2 ${
                                                                isChecked
                                                                    ?
                                                                    "bg-orange-50 border border-orange-300"
                                                                    :
                                                                    "bg-gray-100"
                                                            }`}
                                                        >

                                                            <div
                                                                className="flex justify-start items-center"
                                                            >

                                                                <input
                                                                    type="checkbox"

                                                                    checked={
                                                                        isChecked
                                                                    }

                                                                    onChange={
                                                                        () =>
                                                                            handleSelectedUserToggle(
                                                                                userId
                                                                            )
                                                                    }

                                                                    className="m-1"
                                                                />

                                                                <div
                                                                    className="mx-1"
                                                                >

                                                                    <p
                                                                        className="text-xs font-bold text-gray-700"
                                                                    >
                                                                        {
                                                                            getUserDisplayName(
                                                                                user
                                                                            )
                                                                        }
                                                                    </p>

                                                                    {
                                                                        user
                                                                            ?.email &&
                                                                        (
                                                                            <p
                                                                                className="text-[9px] text-gray-500"
                                                                            >
                                                                                {
                                                                                    user
                                                                                        .email
                                                                                }
                                                                            </p>
                                                                        )
                                                                    }
                                                                </div>
                                                            </div>

                                                            {
                                                                cohortUserItem
                                                                    ?.userCohortStatus &&
                                                                (
                                                                    <p
                                                                        className="text-[8px] bg-white text-gray-600 rounded-full mx-1 px-2 py-1"
                                                                    >
                                                                        {
                                                                            cohortUserItem
                                                                                .userCohortStatus
                                                                        }
                                                                    </p>
                                                                )
                                                            }
                                                        </div>
                                                    )
                                                }
                                            )
                                        }
                                    </div>

                                    {
                                        eligibleCohortUsers
                                            .length ===
                                            0 &&
                                        (
                                            <div
                                                className="bg-gray-100 rounded m-1 p-2"
                                            >
                                                <p
                                                    className="text-[10px] text-gray-600"
                                                >
                                                    No eligible users are currently available in this cohort.
                                                </p>
                                            </div>
                                        )
                                    }
                                </div>
                            )
                        }

                        {/* 8 - Assignment Status */}

                        <div
                            className="bg-white rounded m-1 p-2"
                        >

                            <p
                                className="font-bold text-xs m-1"
                            >
                                Assignment Status
                            </p>

                            <select
                                name="assignmentStatus"

                                value={
                                    formData
                                        .assignmentStatus
                                }

                                onChange={
                                    handleChange
                                }

                                className="w-full rounded p-2 m-1 bg-gray-100"
                            >
                                <option
                                    value="Planned"
                                >
                                    Planned
                                </option>

                                <option
                                    value="Open"
                                >
                                    Open
                                </option>

                                <option
                                    value="Closed"
                                >
                                    Closed
                                </option>

                                <option
                                    value="Archived"
                                >
                                    Archived
                                </option>
                            </select>
                        </div>

                        {/* 9 - Availability */}

                        <div
                            className="bg-white rounded m-1 p-2"
                        >

                            <p
                                className="font-bold text-xs m-1"
                            >
                                Availability
                            </p>

                            <p
                                className="text-[8px] m-1 italic text-gray-600"
                            >
                                Optional. Leave blank when the assignment has no specific availability window.
                            </p>

                            <div
                                className="grid grid-cols-1 md:grid-cols-2 gap-2"
                            >

                                <div>
                                    <p
                                        className="text-[10px] m-1"
                                    >
                                        Available From
                                    </p>

                                    <input
                                        type="datetime-local"

                                        name="availableFrom"

                                        value={
                                            formData
                                                .availableFrom
                                        }

                                        onChange={
                                            handleChange
                                        }

                                        className="w-full rounded p-2 m-1 bg-gray-100"
                                    />
                                </div>

                                <div>
                                    <p
                                        className="text-[10px] m-1"
                                    >
                                        Available Until
                                    </p>

                                    <input
                                        type="datetime-local"

                                        name="availableUntil"

                                        value={
                                            formData
                                                .availableUntil
                                        }

                                        onChange={
                                            handleChange
                                        }

                                        className="w-full rounded p-2 m-1 bg-gray-100"
                                    />
                                </div>
                            </div>
                        </div>

                        {/* 10 - Assignment Reason */}

                        <div
                            className="bg-white rounded m-1 p-2"
                        >

                            <p
                                className="font-bold text-xs m-1"
                            >
                                Assignment Reason
                            </p>

                            <p
                                className="text-[8px] m-1 italic text-gray-600"
                            >
                                Update the reason for this assignment. Changes are retained in the encrypted assignment history.
                            </p>

                            <textarea
                                name="assignmentReason"

                                rows="4"

                                placeholder="Why is this challenge being assigned this way?"

                                value={
                                    formData
                                        .assignmentReason
                                }

                                onChange={
                                    handleChange
                                }

                                className="w-full rounded p-2 m-1 bg-gray-100"
                            />
                        </div>

                        {/* 11 - Current Change History */}

                        <div
                            className="bg-white rounded m-1 p-2"
                        >

                            <div
                                className="flex justify-between items-center"
                            >
                                <div>
                                    <p
                                        className="font-bold text-xs m-1"
                                    >
                                        Assignment Change History
                                    </p>

                                    <p
                                        className="text-[8px] m-1 italic text-gray-600"
                                    >
                                        Existing assignment changes are retained permanently.
                                    </p>
                                </div>

                                <p
                                    className="text-[10px] bg-orange-100 text-orange-700 rounded-full mx-2 px-2 py-1"
                                >
                                    Changes:{" "}
                                    {
                                        assignmentChangeLog
                                            .length
                                    }
                                </p>
                            </div>

                            {
                                assignmentChangeLog
                                    .length >
                                    0 &&
                                (
                                    <div
                                        className="max-h-72 overflow-y-auto"
                                    >
                                        {
                                            assignmentChangeLog.map(
                                                (
                                                    change,
                                                    index
                                                ) => {
                                                    const addedCount =
                                                        change
                                                            ?.selectedUserChanges
                                                            ?.addedUserIds
                                                            ?.length ||
                                                        0

                                                    const removedCount =
                                                        change
                                                            ?.selectedUserChanges
                                                            ?.removedUserIds
                                                            ?.length ||
                                                        0

                                                    return (
                                                        <div
                                                            key={
                                                                change
                                                                    ?._id ||
                                                                change
                                                                    ?.changeNumber ||
                                                                index
                                                            }

                                                            className="bg-gray-100 rounded m-1 p-2"
                                                        >

                                                            <div
                                                                className="flex justify-between items-center"
                                                            >

                                                                <div>
                                                                    <p
                                                                        className="text-[10px] font-bold text-gray-700"
                                                                    >
                                                                        Change{" "}
                                                                        {
                                                                            change
                                                                                ?.changeNumber ||
                                                                            index +
                                                                            1
                                                                        }
                                                                        {" - "}
                                                                        {
                                                                            change
                                                                                ?.changeType ||
                                                                            "Updated"
                                                                        }
                                                                    </p>

                                                                    {
                                                                        change
                                                                            ?.changedBy &&
                                                                        (
                                                                            <p
                                                                                className="text-[9px] text-gray-500"
                                                                            >
                                                                                Changed by:{" "}
                                                                                {
                                                                                    getUserDisplayName(
                                                                                        change
                                                                                            .changedBy
                                                                                    )
                                                                                }
                                                                            </p>
                                                                        )
                                                                    }
                                                                </div>

                                                                <div
                                                                    className="flex justify-end items-center"
                                                                >

                                                                    {
                                                                        addedCount >
                                                                            0 &&
                                                                        (
                                                                            <p
                                                                                className="text-[8px] bg-green-100 text-green-700 rounded-full mx-1 px-2 py-1"
                                                                            >
                                                                                Added:{" "}
                                                                                {
                                                                                    addedCount
                                                                                }
                                                                            </p>
                                                                        )
                                                                    }

                                                                    {
                                                                        removedCount >
                                                                            0 &&
                                                                        (
                                                                            <p
                                                                                className="text-[8px] bg-red-100 text-red-700 rounded-full mx-1 px-2 py-1"
                                                                            >
                                                                                Removed:{" "}
                                                                                {
                                                                                    removedCount
                                                                                }
                                                                            </p>
                                                                        )
                                                                    }
                                                                </div>
                                                            </div>

                                                            {
                                                                change
                                                                    ?.changeReason &&
                                                                (
                                                                    <p
                                                                        className="text-[9px] text-gray-600 mt-1"
                                                                    >
                                                                        {
                                                                            change
                                                                                .changeReason
                                                                        }
                                                                    </p>
                                                                )
                                                            }
                                                        </div>
                                                    )
                                                }
                                            )
                                        }
                                    </div>
                                )
                            }
                        </div>

                        {/* 12 - Active */}

                        <div
                            className="bg-white rounded m-1 p-2"
                        >

                            <div
                                className="flex justify-start items-center bg-gray-100 rounded m-1 p-2"
                            >

                                <input
                                    type="checkbox"

                                    name="active"

                                    checked={
                                        formData
                                            .active
                                    }

                                    onChange={
                                        handleChange
                                    }

                                    className="m-1"
                                />

                                <p
                                    className="text-xs font-bold text-gray-700 m-1"
                                >
                                    Active Assignment
                                </p>
                            </div>
                        </div>

                        {/* 13 - Actions */}

                        <div
                            id="actions"

                            className="bg-white m-1 px-1 py-3 rounded"
                        >

                            <EtBtnFormBasic
                                name={
                                    "Update"
                                }

                                type={
                                    "create"
                                }

                                handleClick={
                                    handleSubmit
                                }
                            />

                            <EtBtnFormBasic
                                name={
                                    "Cancel"
                                }

                                type={
                                    "cancel"
                                }

                                handleClick={
                                    handleCancel
                                }
                            />

                        </div>
                    </form>
                </div>
            </div>
        </div>
    )
}

export default EtUpdateOrgTrailSessionChallengeAssignment